<template>
  <AppLayout>
    <div class="page">
      <Teleport to="body">
        <div v-if="nextBusy" class="global-loading-mask">
          <div class="global-loading-inner">
            <div class="spinner"></div>
            <div class="loading-text">正在上传操作结果，请稍后…</div>
          </div>
        </div>
      </Teleport>
      <Teleport to="body">
        <div v-if="supplementBusy" class="global-loading-mask">
          <div class="global-loading-inner">
            <div class="spinner"></div>
            <div class="loading-text">正在补检，请稍候…</div>
          </div>
        </div>
      </Teleport>

      <SemStepper active-key="step2" />

      <div class="content">
        <aside class="left">
          <div class="left-head">
            <div class="left-title">SEM图片导入</div>
            <button class="ghost" type="button" @click="goPrev">重新导入</button>
          </div>

          <div class="history-block">
            <div class="history-title">历史记录</div>
            <div v-if="historyLoading" class="history-empty">加载中…</div>
            <div v-else-if="!historyTasks.length" class="history-empty">暂无历史记录</div>
            <button
              v-for="h in historyTasks"
              :key="h.task_id"
              type="button"
              class="history-item history-item--preview-card"
              :class="{ active: String(h.task_id) === String(taskId) }"
              @click="openHistoryTask(h.task_id)"
            >
              <div class="history-item-card">
                <div class="history-item-card-header">
                  <span class="history-item-card-id" :title="h.task_id">{{ h.task_id }}</span>
                </div>
                <div
                  class="history-item-card-view"
                  :class="{ 'history-item-card-view--empty': !historyItemPreviewUrl(h) }"
                >
                  <img
                    v-if="historyItemPreviewUrl(h)"
                    class="history-item-card-img"
                    :src="historyItemPreviewUrl(h)"
                    alt=""
                    referrerpolicy="no-referrer"
                  />
                  <span v-else class="history-item-card-ph">暂无图片</span>
                </div>
              </div>
            </button>
          </div>

          <div
            v-if="taskMeta && (taskMeta.materialStructure || taskMeta.materialComposition)"
            class="meta-block"
          >
            <div class="meta-row"><span class="mk">材料结构</span><span class="mv">{{ taskMeta.materialStructure || '—' }}</span></div>
            <div class="meta-row"><span class="mk">材料组成</span><span class="mv">{{ taskMeta.materialComposition || '—' }}</span></div>
            <div v-if="taskMeta.importMode === 'order' && taskMeta.orderId" class="meta-row">
              <span class="mk">订单</span><span class="mv">{{ taskMeta.orderLabel || taskMeta.orderId }}</span>
            </div>
          </div>
        </aside>

        <section class="center">
          <div class="topbar">
            <div class="topbar-right">
              <div class="tool-group">
                <span class="tool-label">显示</span>
                  <select
                    v-model="viewMode"
                    class="tool-select"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                  <option value="filled">填色结果</option>
                  <option value="unfilled">原始上传图</option>
                </select>
              </div>

              <div class="tool-group">
                <span class="tool-label">工具</span>
                  <select
                    v-model="tool"
                    class="tool-select"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                  <option value="hand">移动</option>
                  <option value="arrow">箭头</option>
                  <option value="eraser">擦除</option>
                  <option value="supplement">补检</option>
                  <option value="measure">尺寸线</option>
                </select>
              </div>

              <div class="tool-group size-filter">
                <span class="tool-label">粒径区间过滤</span>
                <input
                  v-model.number="sizeFilter.inputMin"
                  class="range-input"
                  type="number"
                  step="0.01"
                  :disabled="busy || undoBusy || editorBusy || rangeBusy"
                />
                <span class="range-sep">~</span>
                <input
                  v-model.number="sizeFilter.inputMax"
                  class="range-input"
                  type="number"
                  step="0.01"
                  :disabled="busy || undoBusy || editorBusy || rangeBusy"
                />
                <span class="range-unit">{{ scaleUnit }}</span>
                <button
                  type="button"
                  class="tool-btn"
                  @click="applySizeRange"
                  :disabled="busy || undoBusy || editorBusy || rangeBusy"
                >
                  应用
                </button>
                <button
                  type="button"
                  class="tool-btn"
                  @click="resetSizeRange"
                  :disabled="busy || undoBusy || editorBusy || rangeBusy"
                >
                  重置
                </button>
                <span class="range-current">
                  默认:
                  {{ sizeFilter.defaultMin != null && sizeFilter.defaultMax != null ? `${formatRangeNumber(sizeFilter.defaultMin)}~${formatRangeNumber(sizeFilter.defaultMax)} ${scaleUnit}` : '-' }}
                </span>
                <span class="range-current">命中: {{ filteredMasks.length }}</span>
              </div>

              <div class="tool-actions">
                  <button
                    type="button"
                    class="tool-btn"
                    @click="zoomIn"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    class="tool-btn"
                    @click="zoomOut"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    class="tool-btn"
                    @click="resetAllOperations"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                    重置
                  </button>
                  <button
                    type="button"
                    class="tool-btn tool-btn-download"
                    @click="downloadView"
                    :disabled="busy || undoBusy || editorBusy"
                  >
                  下载
                </button>
              </div>
            </div>
          </div>

          <div class="canvas">
            <div class="img-stage" ref="stage">
              <div
                v-if="currentSrc"
                class="img-transform"
                :class="{ 'range-transition': rangeBusy }"
                :style="transformLayerStyle"
              >
                <img
                  class="canvas-img"
                  ref="canvasImg"
                  :src="currentSrc"
                  alt="SEM view"
                  @load="onImgLoad"
                  draggable="false"
                />
                <canvas
                  v-if="viewMode === 'filled'"
                  ref="overlayDisplayCanvas"
                  class="canvas-overlay"
                ></canvas>
              </div>

              <div v-else class="canvas-ph">{{ canvasEmptyHint }}</div>

              <div
                v-if="labelPixels"
                class="hover-layer"
                ref="hoverLayer"
                :style="{ cursor: hoverCursor }"
                @pointermove="onPointerMove"
                @pointerleave="onPointerLeave"
                @pointerdown="onPointerDown"
                @pointerup="onPointerUp"
                @pointercancel="onPointerUp"
                @wheel.prevent="onWheel"
              >
                <svg class="measure-svg">
                  <g v-for="m in measurementStageLines" :key="m.id">
                    <line :x1="m.x1" :y1="m.y1" :x2="m.x2" :y2="m.y2" class="measure-line" />
                    <text :x="m.tx" :y="m.ty" class="measure-text">{{ m.label }}</text>
                  </g>
                  <g v-if="activeMeasurementStageLine">
                    <line
                      :x1="activeMeasurementStageLine.x1"
                      :y1="activeMeasurementStageLine.y1"
                      :x2="activeMeasurementStageLine.x2"
                      :y2="activeMeasurementStageLine.y2"
                      class="measure-line measure-line-active"
                    />
                    <text
                      :x="activeMeasurementStageLine.tx"
                      :y="activeMeasurementStageLine.ty"
                      class="measure-text"
                    >
                      {{ activeMeasurementStageLine.label }}
                    </text>
                  </g>
                </svg>

                <div
                  v-if="hover.visible"
                  class="tooltip"
                  :style="{ left: `${hover.x}px`, top: `${hover.y}px` }"
                >
                  <div class="tt-line">
                    面积：
                    {{ formatMetric3(hover.areaUnit != null ? hover.areaUnit : hover.areaPx) }}
                    {{ hover.areaUnit != null ? hover.areaUnitUnit : 'px^2' }}
                  </div>
                  <div class="tt-line" v-if="hover.equivDiameter != null">
                    等效粒径：{{ formatMetric3(hover.equivDiameter) }} {{ scaleUnit }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="footer">
            <button class="secondary" type="button" @click="goPrev">上一步</button>
            <button
              class="secondary"
              type="button"
              @click="goNext"
              :disabled="busy || nextBusy"
            >
              {{ (busy || nextBusy) ? '填色大约需要1-5分钟，请稍后' : '下一步' }}
            </button>
          </div>
        </section>

        <aside class="right">
          <div class="panel">
            <div class="panel-title">工具栏说明</div>
            <div class="notice">
              可通过顶部工具栏完成查看、筛选与编辑。以下为快捷说明：
            </div>
            <div class="guide-table">
              <div class="row head"><div>功能</div><div>用途</div></div>
              <div class="row"><div>显示</div><div>第二步在此切换「原始上传图」与「AI 填色结果」；第一步仅展示原始图。</div></div>
              <div class="row"><div>工具</div><div>移动、箭头查看、擦除、补检、尺寸线测量。</div></div>
              <div class="row"><div>粒径区间过滤</div><div>设置最小/最大粒径并应用，实时更新统计与分布。</div></div>
              <div class="row"><div>缩放(+/-/重置)</div><div>重置会回到初始状态并清空本页编辑操作。</div></div>
              <div class="row"><div>下载</div><div>导出当前显示图像。</div></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '../../../layouts/AppLayout.vue';
import SemStepper from '../../../components/SemStepper.vue';
import {
  getSemTask,
  getSemTaskHistory,
  getSemTaskSteps,
  runSegmentation,
  saveStep2EditedResult,
  supplementSegmentationByPoint,
} from '../../../services/semApi';

const HARD_CUT_BOTTOM_PX = 160;
const SEM_TASK_STORAGE_KEY = 'sem_current_task_id';

export default {
  name: 'SemStep2Segmentation',
  components: { AppLayout, SemStepper },
  data() {
    return {
      taskId: this.$route.query.taskId || null,
      previewUrl: null,
      scaleValue: 100,
      scaleUnit: 'μm',
      scalePxLength: null,

      overlayUrl: null,
      labelUrl: null,
      overlayDisplayUrl: null,
      stats: null,

      busy: false,
      nextBusy: false,
      ran: false,
      undoBusy: false,
      editorBusy: false,

      // viewer 状态
      viewMode: 'filled', // filled | unfilled
      tool: 'arrow', // hand | arrow | eraser | supplement
      zoom: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
      isErasing: false,
      dragStart: {
        x: 0,
        y: 0,
        panX: 0,
        panY: 0,
      },
      measurements: [],
      drawingMeasure: false,
      activeMeasureStartNat: null,
      activeMeasureEndNat: null,

      // 显示图像映射信息（object-fit: contain）
      imgMeta: {
        naturalW: 0,
        naturalH: 0,
        renderW: 0,
        renderH: 0,
        offsetX: 0,
        offsetY: 0,
      },

      // label 像素数据（用于按像素 id 命中）
      labelPixels: null,
      labelIdArray: null, // Uint32Array: per pixel id
      labelMeta: {
        naturalW: 0,
        naturalH: 0,
      },
      maskMap: {},

      hover: {
        visible: false,
        x: 0,
        y: 0,
        areaPx: null,
        areaUnit: null,
        areaUnitUnit: null,
        equivDiameter: null,
      },

      // 编辑缓存（用于擦除后更新 overlay）
      originalRGBA: null,
      overlayBaseRGBA: null,
      editedOverlayRGBA: null,
      overlayWorkCanvas: null,
      originalWorkCanvas: null,
      idToPositions: null, // Map<number, Uint32Array> : nat pixel positions for each particle id
      erasedIds: null,
      eraseHistory: [],
      currentEraseStepIds: [],
      supplementBusy: false,
      supplementCursorAllowed: false,
      initialResetSnapshot: null,
      overlayNeedsFlush: false,
      overlayFlushRaf: null,
      sizeFilter: {
        min: null,
        max: null,
        inputMin: null,
        inputMax: null,
        defaultMin: null,
        defaultMax: null,
      },
      rangeBusy: false,
      taskMeta: null,
      historyTasks: [],
      historyLoading: true,
    };
  },
  computed: {
    hoverCursor() {
      if (this.tool === 'hand') return this.isPanning ? 'grabbing' : 'grab';
      if (this.tool === 'eraser') return 'crosshair';
      if (this.tool === 'measure') return 'crosshair';
      if (this.tool === 'supplement') return this.supplementCursorAllowed ? 'copy' : 'not-allowed';
      if (this.tool === 'arrow') return 'default';
      return 'default';
    },
    filteredMasks() {
      const masks = this.stats?.masks || [];
      const min = Number(this.sizeFilter.min);
      const max = Number(this.sizeFilter.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return masks;
      return masks.filter((m) => {
        const d = Number(m?.equivDiameter);
        if (!Number.isFinite(d)) return true;
        return this.isWithinClosedRange(d, min, max);
      });
    },
    filteredMaskIdSet() {
      const s = new Set();
      for (const m of this.filteredMasks) {
        if (Number.isFinite(Number(m?.id))) s.add(Number(m.id));
      }
      return s;
    },
    statsSummary() {
      const masks = this.filteredMasks;
      const ds = masks
        .map((m) => Number(m?.equivDiameter))
        .filter((v) => Number.isFinite(v));
      const summary = {
        count: masks.length,
        mean: null,
        std: null,
        p10: null,
        p50: null,
        p90: null,
        unit: this.stats?.summary?.unit || null,
      };
      if (!ds.length) return summary;
      const arr = ds.slice().sort((a, b) => a - b);
      const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
      const variance = arr.reduce((a, b) => a + (b - mean) ** 2, 0) / arr.length;
      const pick = (p) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor((arr.length - 1) * p)))];
      summary.mean = mean;
      summary.std = Math.sqrt(variance);
      summary.p10 = pick(0.10);
      summary.p50 = pick(0.50);
      summary.p90 = pick(0.90);
      return summary;
    },
    filteredHistogram() {
      const ds = this.filteredMasks
        .map((m) => Number(m?.equivDiameter))
        .filter((v) => Number.isFinite(v));
      if (!ds.length) return [];
      const min = Math.min(...ds);
      const max = Math.max(...ds);
      const bins = 10;
      // 全部粒径相同：若仍用 (max-min)/10 会得到 step=1e-9，最后一档 from>to，图会错
      if (min === max) {
        return [{ bin: 1, from: min, to: max, value: ds.length }];
      }
      const step = (max - min) / bins;
      const hist = Array.from({ length: bins }).map((_, i) => ({
        bin: i + 1,
        from: min + i * step,
        to: i === bins - 1 ? max : min + (i + 1) * step,
        value: 0,
      }));
      for (const v of ds) {
        const idx = Math.min(bins - 1, Math.floor((v - min) / step));
        hist[idx].value += 1;
      }
      return hist;
    },
    currentSrc() {
      // 第二步：填色结果用 overlay；原始上传图用 previewUrl（与第一步同源）
      return this.viewMode === 'unfilled' ? this.previewUrl : this.overlayUrl;
    },
    canvasEmptyHint() {
      if (this.viewMode === 'unfilled' && !this.previewUrl) {
        return '暂无原始图地址，请确认任务已保存上传原图';
      }
      if (this.viewMode === 'filled' && !this.overlayUrl) {
        return '等待后端返回填色结果…';
      }
      return '无法加载当前显示图像';
    },
    transformLayerStyle() {
      if (!this.imgMeta.renderW || !this.imgMeta.renderH) {
        return {
          left: '0px',
          top: '0px',
          width: '100%',
          height: '100%',
          transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`,
        };
      }
      return {
        left: `${this.imgMeta.offsetX}px`,
        top: `${this.imgMeta.offsetY}px`,
        width: `${this.imgMeta.renderW}px`,
        height: `${this.imgMeta.renderH}px`,
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`,
      };
    },
    histogramMin() {
      const hist = this.filteredHistogram;
      if (!hist.length) return '-';
      return hist[0].from;
    },
    histogramMax() {
      const hist = this.filteredHistogram;
      if (!hist.length) return '-';
      return hist[hist.length - 1].to;
    },
    measureRatio() {
      if (!this.scalePxLength || !Number.isFinite(Number(this.scalePxLength))) return null;
      const px = Number(this.scalePxLength);
      if (px <= 0) return null;
      return Number(this.scaleValue) / px;
    },
    measurementStageLines() {
      return this.measurements
        .map((m) => this.naturalLineToStage(m.start, m.end, m.id))
        .filter(Boolean);
    },
    activeMeasurementStageLine() {
      if (!this.drawingMeasure || !this.activeMeasureStartNat || !this.activeMeasureEndNat) return null;
      return this.naturalLineToStage(this.activeMeasureStartNat, this.activeMeasureEndNat, 'active');
    },
  },
  mounted() {
    this.resolveTaskId();
    if (!this.taskId) this.$router.push({ name: 'semLanding' });
    this.loadFromDb();
    this.loadTaskHistory();
  },
  methods: {
    historyItemPreviewUrl(h) {
      const u = h?.preview_url || h?.img_url;
      return u && String(u).trim() ? String(u).trim() : null;
    },
    resolveTaskId() {
      if (this.taskId) {
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
        return;
      }
      const cached = sessionStorage.getItem(SEM_TASK_STORAGE_KEY);
      if (cached) {
        this.taskId = cached;
        this.$router.replace({ query: { ...this.$route.query, taskId: cached } });
      }
    },
    async loadTaskHistory() {
      this.historyLoading = true;
      try {
        this.historyTasks = await getSemTaskHistory(50, { segmentStatus: 'filled' });
      } catch (e) {
        console.error(e);
      } finally {
        this.historyLoading = false;
      }
    },
    openHistoryTask(taskId) {
      const next = String(taskId || '').trim();
      if (!next || next === this.taskId) return;
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, next);
      const to = this.$router.resolve({ name: 'semStep2', query: { taskId: next } });
      window.location.assign(to.href);
    },
    async loadFromDb() {
      if (!this.taskId) return;
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      try {
        const [task, steps] = await Promise.all([
          getSemTask(this.taskId),
          getSemTaskSteps(this.taskId),
        ]);
        const parseMaybeJson = (v) => {
          if (!v) return null;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch (e) { return null; }
          }
          return null;
        };
        const trimUrl = (u) => {
          if (u == null || u === '') return null;
          const s = String(u).trim();
          return s || null;
        };
        const segmentSteps = (steps || [])
          .filter((s) => s?.step_type === 'segment' && s?.status === 'success')
          .sort((a, b) => Number(a.step_no || 0) - Number(b.step_no || 0));
        const fullSegRuns = segmentSteps.filter((s) => s.api_path === '/api/sem/segment');
        const latestFullSeg = fullSegRuns.length ? fullSegRuns[fullSegRuns.length - 1] : null;
        // 无填色主图用原图 URL；部分任务表里 img_url 为空但步骤里仍存有 segment 时的 img_url（历史列表缩略图仍可能有图）
        let previewUrl = trimUrl(task?.img_url);
        if (!previewUrl && latestFullSeg) {
          const req = parseMaybeJson(latestFullSeg.request_params) || {};
          previewUrl = trimUrl(req.img_url);
        }
        this.previewUrl = previewUrl;
        if (task?.scale_value !== null && task?.scale_value !== '' && Number.isFinite(Number(task?.scale_value))) {
          this.scaleValue = Number(task.scale_value);
        } else {
          this.scaleValue = 100;
        }
        if (task?.scale_unit) this.scaleUnit = task.scale_unit;
        if (Number.isFinite(Number(task?.scale_px_length))) this.scalePxLength = Number(task.scale_px_length);
        this.taskMeta = {
          materialStructure: task?.material_structure || '',
          materialComposition: task?.material_composition || '',
          importMode: 'local',
        };
        const updateSteps = segmentSteps.filter((s) => s.api_path === '/api/sem/task/update');
        const latestUpdateSeg = updateSteps.length ? updateSteps[updateSteps.length - 1] : null;
        const isLikelyLabelMap = (url) => /(^|\/)label_[^/]*\.png(\?|$)/i.test(String(url || ''));
        if (latestFullSeg) {
          this.overlayUrl = latestFullSeg.overlay_url || null;
          this.labelUrl = latestFullSeg.label_url || null;
          const resp = parseMaybeJson(latestFullSeg.response_data) || {};
          if (resp?.stats) this.stats = resp.stats;
        }
        if (latestUpdateSeg) {
          const resp2 = parseMaybeJson(latestUpdateSeg.response_data) || {};
          if (resp2?.stats) this.stats = resp2.stats;
          const ms2 = resp2?.measurements || null;
          if (ms2 && Array.isArray(ms2.lines)) {
            this.measurements = ms2.lines.map((x) => ({
              id: x.id || `m_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
              start: x.start,
              end: x.end,
            }));
          }
        }
        const taskStats = parseMaybeJson(task?.latest_stats);
        if (taskStats && taskStats.masks) this.stats = taskStats;
        const taskRuler = parseMaybeJson(task?.ruler_info);
        if (
          (!Number.isFinite(Number(this.scalePxLength)) || Number(this.scalePxLength) <= 0)
          && Number.isFinite(Number(taskRuler?.bar?.pxLength || taskRuler?.pxLength))
        ) {
          this.scalePxLength = Number(taskRuler?.bar?.pxLength || taskRuler?.pxLength);
        }
        if (
          (!Number.isFinite(Number(this.scaleValue)) || Number(this.scaleValue) <= 0)
          && Number.isFinite(Number(taskRuler?.scale?.value || taskRuler?.value))
        ) {
          this.scaleValue = Number(taskRuler?.scale?.value || taskRuler?.value);
        }
        if (task?.edited_overlay_url && !isLikelyLabelMap(task.edited_overlay_url)) {
          this.overlayUrl = task.edited_overlay_url;
        }
        const taskMs = parseMaybeJson(task?.measurements);
        if (taskMs && Array.isArray(taskMs.lines)) {
          this.measurements = taskMs.lines.map((x) => ({
            id: x.id || `m_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
            start: x.start,
            end: x.end,
          }));
        }
        if (this.overlayUrl) this.overlayDisplayUrl = this.overlayUrl;
      } catch (e) {
        console.error(e);
      }
      this.prepareFromProvidedOrRun();
    },
    toRangePrecision(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return n;
      return Math.round(n * 1000) / 1000;
    },
    isWithinClosedRange(value, min, max) {
      // 过滤口径与界面显示保持一致：统一按三位小数比较
      const v = this.toRangePrecision(value);
      const lo = this.toRangePrecision(min);
      const hi = this.toRangePrecision(max);
      if (!Number.isFinite(v) || !Number.isFinite(lo) || !Number.isFinite(hi)) return false;
      const eps = 1e-9;
      return v >= lo - eps && v <= hi + eps;
    },
    formatNumber(v) {
      if (v == null || !Number.isFinite(Number(v))) return '-';
      const n = Number(v);
      if (Math.abs(n) >= 100) return n.toFixed(0);
      return n.toFixed(2);
    },
    formatMetric3(v) {
      if (v == null || !Number.isFinite(Number(v))) return '-';
      return Number(v).toFixed(3);
    },
    formatRangeNumber(v) {
      if (v == null || !Number.isFinite(Number(v))) return '-';
      return Number(v).toFixed(3);
    },
    initSizeRangeFromStats() {
      const ds = (this.stats?.masks || [])
        .map((m) => Number(m?.equivDiameter))
        .filter((v) => Number.isFinite(v));
      if (!ds.length) {
        this.sizeFilter.min = null;
        this.sizeFilter.max = null;
        this.sizeFilter.inputMin = null;
        this.sizeFilter.inputMax = null;
        return;
      }
      const min = Math.min(...ds);
      const max = Math.max(...ds);
      const min3 = this.toRangePrecision(min);
      const max3 = this.toRangePrecision(max);
      this.sizeFilter.min = min3;
      this.sizeFilter.max = max3;
      this.sizeFilter.inputMin = min3;
      this.sizeFilter.inputMax = max3;
      this.sizeFilter.defaultMin = min3;
      this.sizeFilter.defaultMax = max3;
    },
    async applySizeRange() {
      const min = Number(this.sizeFilter.inputMin);
      const max = Number(this.sizeFilter.inputMax);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return;
      const nextMin = Math.min(min, max);
      const nextMax = Math.max(min, max);
      const masks = this.stats?.masks || [];
      const hitCount = masks.filter((m) => {
        const d = Number(m?.equivDiameter);
        if (!Number.isFinite(d)) return false;
        return this.isWithinClosedRange(d, nextMin, nextMax);
      }).length;
      if (hitCount === 0) {
        alert('该粒径区间未命中任何颗粒，请调整区间后重试。');
        return;
      }
      this.sizeFilter.min = nextMin;
      this.sizeFilter.max = nextMax;
      this.$nextTick(() => this.drawDistribution());
      await this.rebuildOverlayByFilter({ withTransition: true });
    },
    async resetSizeRange() {
      this.initSizeRangeFromStats();
      if (
        this.stats?.filterMin !== null
        && this.stats?.filterMin !== ''
        && this.stats?.filterMax !== null
        && this.stats?.filterMax !== ''
        && Number.isFinite(Number(this.stats?.filterMin))
        && Number.isFinite(Number(this.stats?.filterMax))
      ) {
        const fmin = Number(this.stats.filterMin);
        const fmax = Number(this.stats.filterMax);
        const lo = Math.min(fmin, fmax);
        const hi = Math.max(fmin, fmax);
        const masks = this.stats?.masks || [];
        const hitCount = masks.filter((m) => {
          const d = Number(m?.equivDiameter);
          return Number.isFinite(d) && d >= lo && d <= hi;
        }).length;
        // 仅当该区间对当前颗粒数据“有效”时才回放，避免历史错误值（如 0~0）污染页面
        if (hitCount > 0) {
          this.sizeFilter.min = lo;
          this.sizeFilter.max = hi;
          this.sizeFilter.inputMin = lo;
          this.sizeFilter.inputMax = hi;
        }
      }
      this.$nextTick(() => this.drawDistribution());
      await this.rebuildOverlayByFilter({ withTransition: true });
    },
    isMaskInCurrentRange(m) {
      if (!m) return false;
      const min = Number(this.sizeFilter.min);
      const max = Number(this.sizeFilter.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return true;
      const d = Number(m.equivDiameter);
      if (!Number.isFinite(d)) return true;
      return this.isWithinClosedRange(d, min, max);
    },

    goPrev() {
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      const to = this.$router.resolve({
        name: 'semStep1',
        query: { taskId: this.taskId },
      });
      window.location.assign(to.href);
    },

    buildStatsFromMasks(masks) {
      const list = Array.isArray(masks) ? masks : [];
      const unit = this.stats?.summary?.unit || this.scaleUnit || null;
      const ds = list
        .map((m) => Number(m?.equivDiameter))
        .filter((v) => Number.isFinite(v));
      const summary = {
        count: list.length,
        mean: null,
        std: null,
        p10: null,
        p50: null,
        p90: null,
        unit,
      };
      if (ds.length) {
        const arr = ds.slice().sort((a, b) => a - b);
        const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
        const variance = arr.reduce((a, b) => a + (b - mean) ** 2, 0) / arr.length;
        const pick = (p) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor((arr.length - 1) * p)))];
        summary.mean = mean;
        summary.std = Math.sqrt(variance);
        summary.p10 = pick(0.10);
        summary.p50 = pick(0.50);
        summary.p90 = pick(0.90);
      }

      const histogram = [];
      if (ds.length) {
        const min = Math.min(...ds);
        const max = Math.max(...ds);
        const bins = 10;
        const step = Math.max(1e-9, (max - min) / bins);
        for (let i = 0; i < bins; i += 1) {
          histogram.push({
            bin: i + 1,
            from: min + i * step,
            to: i === bins - 1 ? max : min + (i + 1) * step,
            value: 0,
          });
        }
        for (const v of ds) {
          const idx = Math.min(bins - 1, Math.floor((v - min) / step));
          histogram[idx].value += 1;
        }
      }

      return { maskCount: list.length, masks: list, histogram, summary };
    },
    getEffectiveMasksForExport() {
      const erased = this.erasedIds || new Set();
      return this.filteredMasks.filter((m) => !erased.has(Number(m?.id)));
    },
    async goNext() {
      if (this.nextBusy) return;
      this.nextBusy = true;
      try {
        // 下一步前固化当前编辑结果：粒径过滤 + 擦除都应进入下一页统计与图像
        const effectiveMasks = this.getEffectiveMasksForExport();
        const exportStats = this.buildStatsFromMasks(effectiveMasks);
        const minRaw = this.sizeFilter.min;
        const maxRaw = this.sizeFilter.max;
        exportStats.filterMin = (minRaw === null || minRaw === '' || !Number.isFinite(Number(minRaw)))
          ? null
          : Number(minRaw);
        exportStats.filterMax = (maxRaw === null || maxRaw === '' || !Number.isFinite(Number(maxRaw)))
          ? null
          : Number(maxRaw);
        try {
          if (this.overlayWorkCanvas) {
            const editedUrl = this.overlayWorkCanvas.toDataURL('image/png');
            const measurementPayload = {
              naturalW: this.imgMeta?.naturalW || this.labelMeta?.naturalW || 0,
              naturalH: this.imgMeta?.naturalH || this.labelMeta?.naturalH || 0,
              lines: (this.measurements || []).map((m) => ({
                id: m.id,
                start: m.start,
                end: m.end,
              })),
            };
            try {
              const saveRes = await saveStep2EditedResult({
                taskId: this.taskId,
                overlayUrl: this.overlayUrl || '',
                editedOverlayDataUrl: editedUrl,
                editedStats: exportStats,
                measurements: measurementPayload,
              });
              void saveRes;
            } catch (e) {
              console.error('saveStep2EditedResult failed:', e);
            }
          }
        } catch (e) {
          console.error(e);
        }

        this.$router.push({
          name: 'semStep3',
          query: { taskId: this.taskId },
        });
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      } finally {
        this.nextBusy = false;
      }
    },

    resetEditingCaches() {
      this.labelPixels = null;
      this.labelIdArray = null;
      this.idToPositions = null;
      this.maskMap = {};
      this.originalRGBA = null;
      this.overlayBaseRGBA = null;
      this.editedOverlayRGBA = null;
      this.overlayWorkCanvas = null;
      this.originalWorkCanvas = null;
      this.erasedIds = null;
      this.eraseHistory = [];
      this.currentEraseStepIds = [];
      this.overlayNeedsFlush = false;
      if (this.overlayFlushRaf) {
        cancelAnimationFrame(this.overlayFlushRaf);
        this.overlayFlushRaf = null;
      }
    },

    prepareFromProvidedOrRun() {
      const hasProvided = !!this.overlayUrl && !!this.labelUrl;
      if (!hasProvided) {
        this.runAi();
        return;
      }

      this.ran = true;

      const masks = this.stats?.masks || [];
      this.maskMap = masks.reduce((acc, m) => {
        acc[m.id] = m;
        return acc;
      }, {});
      this.initSizeRangeFromStats();

      // 等待 overlay img onload 计算 renderW/renderH 后，再初始化 label 像素
      this.busy = true;
      this.$nextTick(async () => {
        try {
          this.drawDistribution();
          await this.initLabelPixels();
          await this.initEditBuffers();
        } finally {
          this.busy = false;
        }
      });
    },

    onImgLoad() {
      const img = this.$refs.canvasImg;
      const stage = this.$refs.stage;
      if (!img || !stage) return;
      this.imgMeta.naturalW = img.naturalWidth || 0;
      this.imgMeta.naturalH = img.naturalHeight || 0;

      const viewW = stage.clientWidth || 0;
      const viewH = stage.clientHeight || 0;
      if (!viewW || !viewH || !this.imgMeta.naturalW || !this.imgMeta.naturalH) return;

      const scale = Math.min(viewW / this.imgMeta.naturalW, viewH / this.imgMeta.naturalH);
      const contentW = this.imgMeta.naturalW * scale;
      const contentH = this.imgMeta.naturalH * scale;

      this.imgMeta.renderW = Math.round(contentW);
      this.imgMeta.renderH = Math.round(contentH);
      this.imgMeta.offsetX = Math.round((viewW - contentW) / 2);
      this.imgMeta.offsetY = Math.round((viewH - contentH) / 2);

      if (this.stats?.histogram?.length) this.drawDistribution();
      this.$nextTick(() => {
        this.refreshVisibleOverlayCanvas();
      });
    },

    async runAi() {
      if (!this.taskId || !this.previewUrl) return;
      if (this.ran) return;

      this.busy = true;
      try {
        let ratio = null;
        if (this.scalePxLength && this.scalePxLength > 0) {
          ratio = this.scaleValue / this.scalePxLength;
        }

        const res = await runSegmentation({
          taskId: this.taskId,
          previewUrl: this.previewUrl,
          ratio,
          unit: this.scaleUnit,
        });

        this.overlayUrl = res.overlayUrl;
        this.labelUrl = res.labelUrl;
        this.stats = res.stats;
        this.ran = true;

        // 构建 mask id -> stats 映射
        const masks = this.stats?.masks || [];
        this.maskMap = masks.reduce((acc, m) => {
          acc[m.id] = m;
          return acc;
        }, {});
        this.initSizeRangeFromStats();

        this.$nextTick(() => {
          this.drawDistribution();
        });

        await this.initLabelPixels();

        // 初始化原图/overlay 像素缓存（用于擦除编辑）
        await this.initEditBuffers();

        this.$nextTick(() => {
          if (this.stats?.histogram?.length) this.drawDistribution();
        });
      } finally {
        this.busy = false;
      }
    },

    async initLabelPixels() {
      if (!this.labelUrl) return;
      // 等到 overlay 图完成加载（用于获得 renderW/renderH，用于像素映射）
      const start = Date.now();
      while ((!this.imgMeta.renderW || !this.imgMeta.renderH) && Date.now() - start < 2000) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 80));
      }
      if (!this.imgMeta.renderW || !this.imgMeta.renderH) return;

      try {
        // 用 fetch(blob -> objectURL) 防止跨域导致 canvas 变脏，无法读取像素
        const rb = await fetch(this.labelUrl, { mode: 'cors' });
        const blob = await rb.blob();
        const objUrl = URL.createObjectURL(blob);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = objUrl;
        });

        const natW = img.naturalWidth || 0;
        const natH = img.naturalHeight || 0;
        if (!natW || !natH) return;

        this.labelMeta.naturalW = natW;
        this.labelMeta.naturalH = natH;

        // 若 overlay naturalW/naturalH 尚未初始化，则同步一下
        if (!this.imgMeta.naturalW) this.imgMeta.naturalW = natW;
        if (!this.imgMeta.naturalH) this.imgMeta.naturalH = natH;

        const canvas = document.createElement('canvas');
        canvas.width = natW;
        canvas.height = natH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, natW, natH);
        const imageData = ctx.getImageData(0, 0, natW, natH);
        this.labelPixels = imageData.data;

        // 为加速擦除/悬浮命中：一次性把每个像素的 id 解码成整数数组
        const buckets = new Map(); // id -> number[] of nat pixel positions
        const idArr = new Uint32Array(natW * natH);
        const cutY = natH > HARD_CUT_BOTTOM_PX ? (natH - HARD_CUT_BOTTOM_PX) : natH;
        for (let pos = 0; pos < natW * natH; pos += 1) {
          const y = Math.floor(pos / natW);
          const pi = pos * 4;
          const r = imageData.data[pi];
          const g = imageData.data[pi + 1];
          const b = imageData.data[pi + 2];
          const id = y >= cutY ? 0 : this.decodeIdFromRgb(r, g, b);
          idArr[pos] = id;
          if (id) {
            const arr = buckets.get(id);
            if (arr) arr.push(pos);
            else buckets.set(id, [pos]);
          }
        }
        this.labelIdArray = idArr;
        this.idToPositions = new Map();
        for (const [id, arr] of buckets.entries()) {
          this.idToPositions.set(id, new Uint32Array(arr));
        }
        // 同步剔除底部裁切区中的统计项，避免“裁切区看不见但统计仍计入”
        const keepIds = new Set(this.idToPositions.keys());
        if (Array.isArray(this.stats?.masks)) {
          this.stats.masks = this.stats.masks.filter((m) => keepIds.has(Number(m?.id)));
          this.stats.maskCount = this.stats.masks.length;
          this.maskMap = this.stats.masks.reduce((acc, m) => {
            acc[m.id] = m;
            return acc;
          }, {});
        }

        URL.revokeObjectURL(objUrl);
      } catch (e) {
        console.error('initLabelPixels failed:', e);
        this.labelPixels = null;
        this.labelIdArray = null;
        this.idToPositions = null;
      }
    },

    async initEditBuffers() {
      // 已初始化过就直接返回（但如果 overlayUrl 变更，这里也可扩展）
      if (this.originalRGBA && this.editedOverlayRGBA && this.overlayWorkCanvas) return;
      if (!this.previewUrl || !this.overlayUrl) return;

      // 确保已经拿到 label 的 natural 尺寸
      const natW = this.labelMeta.naturalW || this.imgMeta.naturalW;
      const natH = this.labelMeta.naturalH || this.imgMeta.naturalH;
      if (!natW || !natH) return;

      this.erasedIds = new Set();
      this.eraseHistory = [];
      this.currentEraseStepIds = [];

      const loadToRGBA = async (url) => {
        const rb = await fetch(url, { mode: 'cors' });
        if (!rb.ok) throw new Error(`Failed to load image: ${rb.status}`);
        const blob = await rb.blob();
        const objUrl = URL.createObjectURL(blob);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = objUrl;
        });

        const canvas = document.createElement('canvas');
        canvas.width = natW;
        canvas.height = natH;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas 2d not available');
        ctx.drawImage(img, 0, 0, natW, natH);
        const imageData = ctx.getImageData(0, 0, natW, natH);
        URL.revokeObjectURL(objUrl);
        return imageData.data;
      };

      try {
        // 原图（用于擦除后的回填）
        const original = await loadToRGBA(this.previewUrl);
        this.originalRGBA = new Uint8ClampedArray(original);

        // 填色 overlay（可被进一步擦除修改）
        const overlay = await loadToRGBA(this.overlayUrl);
        this.overlayBaseRGBA = new Uint8ClampedArray(overlay);
        this.editedOverlayRGBA = new Uint8ClampedArray(overlay);
        // 兼容历史 overlay：前端再次强制裁切底部，保证裁切区绝不显示填色
        const cutY = natH > HARD_CUT_BOTTOM_PX ? (natH - HARD_CUT_BOTTOM_PX) : natH;
        for (let y = cutY; y < natH; y += 1) {
          for (let x = 0; x < natW; x += 1) {
            const i = (y * natW + x) * 4;
            this.overlayBaseRGBA[i] = this.originalRGBA[i];
            this.overlayBaseRGBA[i + 1] = this.originalRGBA[i + 1];
            this.overlayBaseRGBA[i + 2] = this.originalRGBA[i + 2];
            this.overlayBaseRGBA[i + 3] = this.originalRGBA[i + 3];
            this.editedOverlayRGBA[i] = this.originalRGBA[i];
            this.editedOverlayRGBA[i + 1] = this.originalRGBA[i + 1];
            this.editedOverlayRGBA[i + 2] = this.originalRGBA[i + 2];
            this.editedOverlayRGBA[i + 3] = this.originalRGBA[i + 3];
          }
        }

        // 画布缓存：用于快速更新 + 下载
        const originalCanvas = document.createElement('canvas');
        originalCanvas.width = natW;
        originalCanvas.height = natH;
        const originalCtx = originalCanvas.getContext('2d');
        if (!originalCtx) throw new Error('original canvas ctx not available');
        originalCtx.putImageData(new ImageData(this.originalRGBA, natW, natH), 0, 0);

        const overlayCanvas = document.createElement('canvas');
        overlayCanvas.width = natW;
        overlayCanvas.height = natH;
        const overlayCtx = overlayCanvas.getContext('2d');
        if (!overlayCtx) throw new Error('overlay canvas ctx not available');
        overlayCtx.putImageData(new ImageData(this.editedOverlayRGBA, natW, natH), 0, 0);

        this.originalWorkCanvas = originalCanvas;
        this.overlayWorkCanvas = overlayCanvas;
        this.overlayDisplayUrl = this.overlayUrl;
        this.$nextTick(() => {
          this.refreshVisibleOverlayCanvas();
        });
        await this.rebuildOverlayByFilter({ withTransition: false });
        this.captureInitialResetSnapshot();
      } catch (e) {
        console.error('initEditBuffers failed:', e);
      }
    },

    stageToNatural(p) {
      const natW = this.imgMeta.naturalW;
      const natH = this.imgMeta.naturalH;
      if (!natW || !natH) return null;
      if (!this.imgMeta.renderW || !this.imgMeta.renderH || !this.zoom) return null;

      // transformLayer: left=offsetX/top=offsetY, 内部 translate(panX/panY) + scale(zoom)
      const baseScaleX = this.imgMeta.renderW / natW;
      const baseScaleY = this.imgMeta.renderH / natH;

      const natX = (p.x - this.imgMeta.offsetX - this.panX) / (baseScaleX * this.zoom);
      const natY = (p.y - this.imgMeta.offsetY - this.panY) / (baseScaleY * this.zoom);

      const x = Math.floor(natX);
      const y = Math.floor(natY);
      if (x < 0 || y < 0 || x >= natW || y >= natH) return null;
      return { x, y };
    },

    naturalToStage(natX, natY) {
      const natW = this.imgMeta.naturalW;
      const natH = this.imgMeta.naturalH;
      if (!natW || !natH) return null;
      if (!this.imgMeta.renderW || !this.imgMeta.renderH) return null;

      const baseScaleX = this.imgMeta.renderW / natW;
      const baseScaleY = this.imgMeta.renderH / natH;
      return {
        x: this.imgMeta.offsetX + this.panX + natX * baseScaleX * this.zoom,
        y: this.imgMeta.offsetY + this.panY + natY * baseScaleY * this.zoom,
      };
    },

    formatMeasureLabel(pxLen) {
      if (!Number.isFinite(pxLen)) return '-';
      if (this.measureRatio != null) {
        const v = pxLen * this.measureRatio;
        return `${this.formatMetric3(v)} ${this.scaleUnit}`;
      }
      return `${this.formatMetric3(pxLen)} ${this.scaleUnit || 'μm'}`;
    },

    naturalLineToStage(start, end, id) {
      if (!start || !end) return null;
      const p1 = this.naturalToStage(start.x, start.y);
      const p2 = this.naturalToStage(end.x, end.y);
      if (!p1 || !p2) return null;
      const pxLen = Math.hypot(end.x - start.x, end.y - start.y);
      return {
        id,
        x1: p1.x,
        y1: p1.y,
        x2: p2.x,
        y2: p2.y,
        tx: (p1.x + p2.x) / 2 + 6,
        ty: (p1.y + p2.y) / 2 - 6,
        label: this.formatMeasureLabel(pxLen),
      };
    },

    decodeIdAtNat(natX, natY) {
      if (!this.labelIdArray) return 0;
      const natW = this.imgMeta.naturalW;
      const pos = natY * natW + natX;
      return this.labelIdArray[pos] || 0;
    },

    isSupplementAllowedAtNatural(nat) {
      if (!nat) return false;
      const natH = this.imgMeta.naturalH || this.labelMeta.naturalH || 0;
      if (!natH) return false;
      // 与后端保持一致：底部 hard-cut 区域不允许补检
      const cutY = Math.max(0, natH - HARD_CUT_BOTTOM_PX);
      if (nat.y >= cutY) return false;
      // 已填色区域不参与补检
      const existId = this.decodeIdAtNat(nat.x, nat.y);
      return !existId;
    },

    captureInitialResetSnapshot() {
      if (!this.overlayBaseRGBA || !this.labelIdArray || !this.idToPositions) return;
      const idMap = new Map();
      for (const [id, arr] of this.idToPositions.entries()) {
        idMap.set(Number(id), new Uint32Array(arr));
      }
      this.initialResetSnapshot = {
        overlayBaseRGBA: new Uint8ClampedArray(this.overlayBaseRGBA),
        labelIdArray: new Uint32Array(this.labelIdArray),
        idToPositions: idMap,
        maskMap: JSON.parse(JSON.stringify(this.maskMap || {})),
        stats: JSON.parse(JSON.stringify(this.stats || { masks: [], summary: {}, histogram: [] })),
        measurements: JSON.parse(JSON.stringify(this.measurements || [])),
      };
    },

    async supplementAtNaturalPoint(nat) {
      if (!nat || this.supplementBusy) return;
      if (!this.previewUrl || !this.overlayBaseRGBA || !this.editedOverlayRGBA) return;
      if (!this.idToPositions || !this.labelIdArray) return;
      if (!this.isSupplementAllowedAtNatural(nat)) return;
      const existId = this.decodeIdAtNat(nat.x, nat.y);
      this.supplementBusy = true;
      try {
        const ratio = this.measureRatio;
        const res = await supplementSegmentationByPoint({
          previewUrl: this.previewUrl,
          pointX: nat.x,
          pointY: nat.y,
          ratio: Number.isFinite(Number(ratio)) ? Number(ratio) : null,
          unit: this.scaleUnit,
          minAreaPx: 0,
        });
        if (!res || !res.maskPngBase64) {
          window.alert('该位置未补检到有效颗粒，请换一个点重试');
          return;
        }

        const natW = this.imgMeta.naturalW || this.labelMeta.naturalW;
        const natH = this.imgMeta.naturalH || this.labelMeta.naturalH;
        if (!natW || !natH) return;

        const dataUrl = `data:image/png;base64,${res.maskPngBase64}`;
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = dataUrl;
        });
        const c = document.createElement('canvas');
        c.width = natW;
        c.height = natH;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, natW, natH);
        const d = ctx.getImageData(0, 0, natW, natH).data;

        const nextId = Object.keys(this.maskMap || {})
          .map((k) => Number(k))
          .filter((v) => Number.isFinite(v))
          .reduce((m, v) => Math.max(m, v), 0) + 1;

        const color = [
          (nextId * 53) % 255,
          (nextId * 97) % 255,
          (nextId * 193) % 255,
        ];
        const positions = [];
        for (let pos = 0; pos < natW * natH; pos += 1) {
          const i = pos * 4;
          if (d[i] < 127) continue;
          positions.push(pos);
          this.labelIdArray[pos] = nextId;
          this.overlayBaseRGBA[i] = this.overlayBaseRGBA[i] * 0.5 + color[0] * 0.5;
          this.overlayBaseRGBA[i + 1] = this.overlayBaseRGBA[i + 1] * 0.5 + color[1] * 0.5;
          this.overlayBaseRGBA[i + 2] = this.overlayBaseRGBA[i + 2] * 0.5 + color[2] * 0.5;
          this.overlayBaseRGBA[i + 3] = 255;
          this.editedOverlayRGBA[i] = this.overlayBaseRGBA[i];
          this.editedOverlayRGBA[i + 1] = this.overlayBaseRGBA[i + 1];
          this.editedOverlayRGBA[i + 2] = this.overlayBaseRGBA[i + 2];
          this.editedOverlayRGBA[i + 3] = this.overlayBaseRGBA[i + 3];
        }
        if (!positions.length) {
          window.alert('补检区域为空，请重试');
          return;
        }
        if (existId && this.idToPositions.has(existId)) {
          // 在已有颗粒上补检：从旧 id 的像素索引里扣除新 mask，避免后续重建 overlay 时重叠覆盖
          const covered = new Set(positions);
          const old = this.idToPositions.get(existId);
          const kept = [];
          for (let i = 0; i < old.length; i += 1) {
            const p = old[i];
            if (!covered.has(p)) kept.push(p);
          }
          this.idToPositions.set(existId, new Uint32Array(kept));
        }
        this.idToPositions.set(nextId, new Uint32Array(positions));
        this.maskMap[nextId] = {
          id: nextId,
          areaPx: Number(res.areaPx) || positions.length,
          areaUnit: Number.isFinite(Number(res.areaUnit)) ? Number(res.areaUnit) : null,
          areaUnitUnit: res.areaUnitUnit || null,
          equivDiameter: Number.isFinite(Number(res.equivDiameter)) ? Number(res.equivDiameter) : null,
          centroid: res.centroid || null,
          unit: res.unit || null,
        };
        if (!this.stats) this.stats = { masks: [], summary: {}, histogram: [] };
        if (!Array.isArray(this.stats.masks)) this.stats.masks = [];
        const prevRange = {
          min: this.sizeFilter.min,
          max: this.sizeFilter.max,
          inputMin: this.sizeFilter.inputMin,
          inputMax: this.sizeFilter.inputMax,
          defaultMin: this.sizeFilter.defaultMin,
          defaultMax: this.sizeFilter.defaultMax,
        };
        this.stats.masks.push(this.maskMap[nextId]);
        this.stats.maskCount = this.stats.masks.length;
        const hasCustomRange = (
          Number.isFinite(Number(prevRange.min))
          && Number.isFinite(Number(prevRange.max))
          && Number.isFinite(Number(prevRange.defaultMin))
          && Number.isFinite(Number(prevRange.defaultMax))
          && (
            Math.abs(Number(prevRange.min) - Number(prevRange.defaultMin)) > 1e-9
            || Math.abs(Number(prevRange.max) - Number(prevRange.defaultMax)) > 1e-9
          )
        );
        // 重新计算默认粒径区间，保证“默认/命中/分布”都基于最新 masks
        this.initSizeRangeFromStats();
        // 若用户此前手动改过区间，尽量保留其当前筛选口径
        if (hasCustomRange) {
          this.sizeFilter.min = prevRange.min;
          this.sizeFilter.max = prevRange.max;
          this.sizeFilter.inputMin = prevRange.inputMin;
          this.sizeFilter.inputMax = prevRange.inputMax;
        }
        this.overlayNeedsFlush = true;
        await this.rebuildOverlayByFilter({ withTransition: false });
        this.$nextTick(() => this.drawDistribution());
      } catch (e) {
        console.error('supplementAtNaturalPoint failed:', e);
        window.alert('补检失败，请稍后重试');
      } finally {
        this.supplementBusy = false;
      }
    },

    async resetAllOperations() {
      // 统一重置入口：缩放、筛选、擦除、补检、尺寸线
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.tool = 'arrow';
      this.hover.visible = false;
      this.supplementCursorAllowed = false;
      if (!this.initialResetSnapshot || !this.overlayBaseRGBA || !this.editedOverlayRGBA) {
        this.initSizeRangeFromStats();
        await this.rebuildOverlayByFilter({ withTransition: false });
        return;
      }
      this.overlayBaseRGBA.set(this.initialResetSnapshot.overlayBaseRGBA);
      this.editedOverlayRGBA.set(this.initialResetSnapshot.overlayBaseRGBA);
      this.labelIdArray = new Uint32Array(this.initialResetSnapshot.labelIdArray);
      const idMap = new Map();
      for (const [id, arr] of this.initialResetSnapshot.idToPositions.entries()) {
        idMap.set(Number(id), new Uint32Array(arr));
      }
      this.idToPositions = idMap;
      this.maskMap = JSON.parse(JSON.stringify(this.initialResetSnapshot.maskMap || {}));
      this.stats = JSON.parse(JSON.stringify(this.initialResetSnapshot.stats || { masks: [], summary: {}, histogram: [] }));
      this.measurements = JSON.parse(JSON.stringify(this.initialResetSnapshot.measurements || []));
      if (!this.erasedIds) this.erasedIds = new Set();
      this.erasedIds.clear();
      this.eraseHistory = [];
      this.currentEraseStepIds = [];
      this.initSizeRangeFromStats();
      this.overlayNeedsFlush = true;
      await this.rebuildOverlayByFilter({ withTransition: false });
      this.$nextTick(() => this.drawDistribution());
    },

    clearMeasures() {
      this.measurements = [];
      this.drawingMeasure = false;
      this.activeMeasureStartNat = null;
      this.activeMeasureEndNat = null;
    },

    async rebuildOverlayByFilter({ withTransition = false } = {}) {
      if (!this.overlayBaseRGBA || !this.editedOverlayRGBA || !this.originalRGBA) return;
      if (!this.idToPositions || !this.overlayWorkCanvas) return;
      if (this.editorBusy) return;

      if (withTransition) {
        this.rangeBusy = true;
        await this.$nextTick();
        await new Promise((r) => requestAnimationFrame(() => r()));
      }

      this.editedOverlayRGBA.set(this.overlayBaseRGBA);

      const hideIds = new Set();
      for (const key of Object.keys(this.maskMap || {})) {
        const id = Number(key);
        if (!Number.isFinite(id)) continue;
        if (!this.filteredMaskIdSet.has(id)) hideIds.add(id);
      }
      if (this.erasedIds && this.erasedIds.size) {
        for (const id of this.erasedIds) hideIds.add(Number(id));
      }

      for (const id of hideIds) {
        const positions = this.idToPositions.get(id);
        if (!positions || positions.length === 0) continue;
        for (let k = 0; k < positions.length; k += 1) {
          const pos = positions[k];
          const i = pos * 4;
          this.editedOverlayRGBA[i] = this.originalRGBA[i];
          this.editedOverlayRGBA[i + 1] = this.originalRGBA[i + 1];
          this.editedOverlayRGBA[i + 2] = this.originalRGBA[i + 2];
          this.editedOverlayRGBA[i + 3] = this.originalRGBA[i + 3];
        }
      }

      this.overlayNeedsFlush = true;
      await this.flushOverlayIfNeeded();

      if (withTransition) {
        await new Promise((r) => setTimeout(r, 180));
        this.rangeBusy = false;
      }
    },

    async updateOverlayDisplayFromWorkCanvas() {
      // 改为前端 canvas 直绘，避免频繁 toBlob 带来的卡顿
      this.refreshVisibleOverlayCanvas();
    },

    refreshVisibleOverlayCanvas() {
      if (!this.overlayWorkCanvas) return;
      const c = this.$refs.overlayDisplayCanvas;
      if (!c) return;
      const natW = this.imgMeta.naturalW || this.labelMeta.naturalW;
      const natH = this.imgMeta.naturalH || this.labelMeta.naturalH;
      if (!natW || !natH) return;

      if (c.width !== natW) c.width = natW;
      if (c.height !== natH) c.height = natH;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, natW, natH);
      ctx.drawImage(this.overlayWorkCanvas, 0, 0, natW, natH);
    },

    scheduleOverlayFlush() {
      if (!this.overlayNeedsFlush) return;
      if (this.overlayFlushRaf) return;
      this.overlayFlushRaf = requestAnimationFrame(() => {
        this.overlayFlushRaf = null;
        // 不阻塞事件流
        void this.flushOverlayIfNeeded();
      });
    },

    async flushOverlayIfNeeded() {
      if (!this.overlayNeedsFlush) return;
      if (this.editorBusy) return;
      if (!this.overlayWorkCanvas || !this.editedOverlayRGBA) return;

      this.overlayNeedsFlush = false;
      const natW = this.imgMeta.naturalW;
      const natH = this.imgMeta.naturalH;
      if (!natW || !natH) return;

      this.editorBusy = true;
      try {
        const ctx = this.overlayWorkCanvas.getContext('2d');
        if (ctx) {
          ctx.putImageData(new ImageData(this.editedOverlayRGBA, natW, natH), 0, 0);
        }
        await this.updateOverlayDisplayFromWorkCanvas();
      } finally {
        this.editorBusy = false;
      }
    },

    async eraseParticleById(id) {
      if (!id || !this.erasedIds) return;
      if (this.erasedIds.has(id)) return;
      if (!this.originalRGBA || !this.editedOverlayRGBA || !this.overlayWorkCanvas) return;
      if (!this.idToPositions) return;

      const positions = this.idToPositions.get(id);
      if (!positions || positions.length === 0) return;

      // 只更新“该粒子”的像素点：避免每次都全图扫描 natW*natH
      for (let k = 0; k < positions.length; k += 1) {
        const pos = positions[k];
        const i = pos * 4;
        this.editedOverlayRGBA[i] = this.originalRGBA[i];
        this.editedOverlayRGBA[i + 1] = this.originalRGBA[i + 1];
        this.editedOverlayRGBA[i + 2] = this.originalRGBA[i + 2];
        this.editedOverlayRGBA[i + 3] = this.originalRGBA[i + 3];
      }

      this.erasedIds.add(id);
      if (Array.isArray(this.currentEraseStepIds)) this.currentEraseStepIds.push(id);
      // 擦除过程中先只改内存，抬手后统一刷新，避免频繁 toBlob 卡顿
      this.overlayNeedsFlush = true;
      // 实时反馈：直接把 work canvas 画到可见 canvas
      this.refreshVisibleOverlayCanvas();
    },

    async applyErasedIdsToOverlay() {
      if (!this.overlayBaseRGBA || !this.editedOverlayRGBA || !this.overlayWorkCanvas) return;
      if (!this.originalRGBA || !this.labelIdArray || !this.erasedIds) return;

      const natW = this.imgMeta.naturalW;
      const natH = this.imgMeta.naturalH;
      if (!natW || !natH) return;

      // 先还原为“未擦除”的 overlay
      this.editedOverlayRGBA.set(this.overlayBaseRGBA);

      // 再把当前 erasedIds 的颗粒回填为原图像素
      const total = natW * natH;
      for (let pos = 0; pos < total; pos += 1) {
        const id = this.labelIdArray[pos];
        if (!this.erasedIds.has(id)) continue;
        const i = pos * 4;
        this.editedOverlayRGBA[i] = this.originalRGBA[i];
        this.editedOverlayRGBA[i + 1] = this.originalRGBA[i + 1];
        this.editedOverlayRGBA[i + 2] = this.originalRGBA[i + 2];
        this.editedOverlayRGBA[i + 3] = this.originalRGBA[i + 3];
      }

      const ctx = this.overlayWorkCanvas.getContext('2d');
      if (ctx) {
        ctx.putImageData(new ImageData(this.editedOverlayRGBA, natW, natH), 0, 0);
      }
      await this.updateOverlayDisplayFromWorkCanvas();
    },

    async undoEraseStep() {
      if (!this.eraseHistory.length || !this.erasedIds) return;
      if (this.undoBusy || this.editorBusy) return;

      this.undoBusy = true;
      try {
        const step = this.eraseHistory.pop() || [];
        if (!this.idToPositions) return;
        for (const id of step) {
          this.erasedIds.delete(id);
          const positions = this.idToPositions.get(id);
          if (!positions || positions.length === 0) continue;
          for (let k = 0; k < positions.length; k += 1) {
            const pos = positions[k];
            const i = pos * 4;
            // 撤回时恢复“填色前的 overlay 基线色”，而不是原图色
            this.editedOverlayRGBA[i] = this.overlayBaseRGBA[i];
            this.editedOverlayRGBA[i + 1] = this.overlayBaseRGBA[i + 1];
            this.editedOverlayRGBA[i + 2] = this.overlayBaseRGBA[i + 2];
            this.editedOverlayRGBA[i + 3] = this.overlayBaseRGBA[i + 3];
          }
        }

        this.overlayNeedsFlush = true;
        await this.rebuildOverlayByFilter({ withTransition: false });
      } finally {
        this.undoBusy = false;
      }
    },

    async undoEraseAll() {
      if (!this.erasedIds || this.erasedIds.size === 0) return;
      if (this.undoBusy || this.editorBusy) return;
      if (!this.overlayBaseRGBA || !this.editedOverlayRGBA) return;

      this.undoBusy = true;
      try {
        this.erasedIds.clear();
        this.eraseHistory = [];
        this.currentEraseStepIds = [];

        // 直接恢复完整 overlay 基线图，最快
        this.editedOverlayRGBA.set(this.overlayBaseRGBA);
        this.overlayNeedsFlush = true;
        await this.rebuildOverlayByFilter({ withTransition: false });
      } finally {
        this.undoBusy = false;
      }
    },

    zoomIn() {
      this.setZoomAtCenter(this.zoom * 1.15);
    },
    zoomOut() {
      this.setZoomAtCenter(this.zoom / 1.15);
    },
    resetView() {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
    },

    setZoomAtCenter(nextZoom) {
      const z = Math.max(0.4, Math.min(5, nextZoom));
      this.zoom = z;
    },

    onWheel(evt) {
      if (this.tool === 'eraser') return;
      const layer = this.$refs.hoverLayer;
      if (!layer) return;
      if (!this.imgMeta.renderW || !this.imgMeta.renderH) return;

      const rect = layer.getBoundingClientRect();
      const p = { x: evt.clientX - rect.left, y: evt.clientY - rect.top };

      const dir = evt.deltaY > 0 ? -1 : 1;
      const factor = dir > 0 ? 1.12 : 1 / 1.12;
      const nextZoom = Math.max(0.4, Math.min(5, this.zoom * factor));
      if (nextZoom === this.zoom) return;

      const natW = this.imgMeta.naturalW;
      const natH = this.imgMeta.naturalH;
      if (!natW || !natH) return;

      const baseScaleX = this.imgMeta.renderW / natW;
      const baseScaleY = this.imgMeta.renderH / natH;

      const natX = (p.x - this.imgMeta.offsetX - this.panX) / (baseScaleX * this.zoom);
      const natY = (p.y - this.imgMeta.offsetY - this.panY) / (baseScaleY * this.zoom);

      // 保持滚轮下方点的自然坐标不变
      this.panX = p.x - this.imgMeta.offsetX - natX * baseScaleX * nextZoom;
      this.panY = p.y - this.imgMeta.offsetY - natY * baseScaleY * nextZoom;
      this.zoom = nextZoom;
    },

    onPointerDown(evt) {
      if (this.tool === 'hand') {
        this.isPanning = true;
        this.dragStart.x = evt.clientX;
        this.dragStart.y = evt.clientY;
        this.dragStart.panX = this.panX;
        this.dragStart.panY = this.panY;
        return;
      }

      if (this.tool === 'measure') {
        const p = this.getLocalPoint(evt);
        const nat = this.stageToNatural(p);
        if (!nat) return;
        this.drawingMeasure = true;
        this.activeMeasureStartNat = nat;
        this.activeMeasureEndNat = { ...nat };
        return;
      }

      if (this.tool === 'eraser') {
        if (this.viewMode !== 'filled') this.viewMode = 'filled';
        this.isErasing = true;
        this.currentEraseStepIds = [];
        this.eraseAtEvent(evt);
        return;
      }

      if (this.tool === 'supplement') {
        if (this.viewMode !== 'filled') this.viewMode = 'filled';
        const p = this.getLocalPoint(evt);
        const nat = this.stageToNatural(p);
        if (!nat) return;
        if (!this.isSupplementAllowedAtNatural(nat)) return;
        void this.supplementAtNaturalPoint(nat);
      }
    },

    onPointerUp() {
      if (this.drawingMeasure && this.activeMeasureStartNat && this.activeMeasureEndNat) {
        const s = this.activeMeasureStartNat;
        const e = this.activeMeasureEndNat;
        const len = Math.hypot(e.x - s.x, e.y - s.y);
        if (len >= 3) {
          this.measurements.push({
            id: `m_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
            start: { ...s },
            end: { ...e },
          });
        }
      }
      this.drawingMeasure = false;
      this.activeMeasureStartNat = null;
      this.activeMeasureEndNat = null;

      // 一次 pointer down -> up 视为一个擦除步骤
      if (this.isErasing && Array.isArray(this.currentEraseStepIds) && this.currentEraseStepIds.length) {
        const uniq = Array.from(new Set(this.currentEraseStepIds));
        this.eraseHistory.push(uniq);
      }
      this.currentEraseStepIds = [];

      // 确保本次擦除的最终结果立即落盘（避免用户抬起后看到延迟）
      if (this.overlayNeedsFlush) void this.flushOverlayIfNeeded();

      this.isPanning = false;
      this.isErasing = false;
    },

    eraseAtEvent(evt) {
      const p = this.getLocalPoint(evt);
      const nat = this.stageToNatural(p);
      if (!nat) return;
      const id = this.decodeIdAtNat(nat.x, nat.y);
      if (!id) return;
      this.eraseParticleById(id);
    },

    async downloadView() {
      try {
        if (!this.originalWorkCanvas || !this.overlayWorkCanvas) return;
        const canvas = this.viewMode === 'unfilled' ? this.originalWorkCanvas : this.overlayWorkCanvas;
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sem_particles_${this.viewMode}_${this.taskId || 'task'}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('downloadView failed:', e);
      }
    },

    drawDistribution() {
      const canvas = this.$refs.distCanvas;
      if (!canvas) return;
      const hist = this.filteredHistogram;

      const cssW = canvas.clientWidth || 1;
      const cssH = canvas.clientHeight || 1;
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      if (!hist.length) return;

      const marginL = 46;
      const marginR = 12;
      const marginT = 12;
      const marginB = 42;

      const usableW = cssW - marginL - marginR;
      const usableH = cssH - marginT - marginB;
      const baselineY = marginT + usableH;

      const minX = hist[0].from;
      const maxX = hist[hist.length - 1].to;
      const maxV = Math.max(...hist.map((b) => Number(b.value) || 0), 1);
      const rangeX = Math.max(1e-9, maxX - minX);

      ctx.save();
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.18)';
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        const y = marginT + (usableH * i) / 4;
        ctx.beginPath();
        ctx.moveTo(marginL, y);
        ctx.lineTo(cssW - marginR, y);
        ctx.stroke();
      }
      ctx.restore();

      const xMap = (x) => marginL + ((x - minX) / rangeX) * usableW;
      const yMap = (v) => baselineY - (v / maxV) * usableH;

      // 坐标轴与刻度
      ctx.save();
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.75)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(marginL, marginT);
      ctx.lineTo(marginL, baselineY);
      ctx.lineTo(cssW - marginR, baselineY);
      ctx.stroke();

      ctx.fillStyle = 'rgba(71, 85, 105, 0.95)';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 4; i += 1) {
        const y = baselineY - (usableH * i) / 4;
        const v = (maxV * i) / 4;
        ctx.beginPath();
        ctx.moveTo(marginL - 4, y);
        ctx.lineTo(marginL, y);
        ctx.stroke();
        ctx.fillText(String(Math.round(v)), marginL - 6, y);
      }

      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let i = 0; i <= 4; i += 1) {
        const x = marginL + (usableW * i) / 4;
        const v = minX + (rangeX * i) / 4;
        ctx.beginPath();
        ctx.moveTo(x, baselineY);
        ctx.lineTo(x, baselineY + 4);
        ctx.stroke();
        ctx.fillText(this.formatRangeNumber(v), x, baselineY + 6);
      }

      // 轴标题
      const unit = this.statsSummary?.unit || this.scaleUnit || 'μm';
      ctx.fillText(`粒径 (${unit})`, marginL + usableW / 2, cssH - 14);
      ctx.save();
      ctx.translate(13, marginT + usableH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('数量', 0, 0);
      ctx.restore();
      ctx.restore();

      ctx.save();
      ctx.fillStyle = 'rgba(59, 130, 246, 0.18)';
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.65)';
      ctx.lineWidth = 1.5;

      const first = hist[0];
      ctx.beginPath();
      ctx.moveTo(xMap(first.from), baselineY);
      for (const b of hist) {
        const xL = xMap(b.from);
        const xR = xMap(b.to);
        const y = yMap(Number(b.value) || 0);

        ctx.lineTo(xL, y);
        ctx.lineTo(xR, y);
      }
      const last = hist[hist.length - 1];
      ctx.lineTo(xMap(last.to), baselineY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      hist.forEach((b, i) => {
        const xL = xMap(b.from);
        const xR = xMap(b.to);
        const xMid = (xL + xR) / 2;
        const y = yMap(Number(b.value) || 0);
        if (i === 0) ctx.moveTo(xMid, y);
        else ctx.lineTo(xMid, y);
      });
      ctx.stroke();

      ctx.fillStyle = 'rgba(236, 253, 245, 0.95)';
      hist.forEach((b) => {
        const xMid = (xMap(b.from) + xMap(b.to)) / 2;
        const y = yMap(Number(b.value) || 0);
        ctx.beginPath();
        ctx.arc(xMid, y, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.85)';
        ctx.stroke();
      });
      ctx.restore();
    },

    getLocalPoint(evt) {
      const layer = this.$refs.hoverLayer;
      if (!layer) return { x: 0, y: 0 };
      const rect = layer.getBoundingClientRect();
      return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    },

    decodeIdFromRgb(r, g, b) {
      return r + (g << 8) + (b << 16);
    },

    onPointerLeave() {
      this.hover.visible = false;
      this.supplementCursorAllowed = false;
    },

    onPointerMove(evt) {
      const p = this.getLocalPoint(evt);

      if (this.isPanning) {
        const dx = evt.clientX - this.dragStart.x;
        const dy = evt.clientY - this.dragStart.y;
        this.panX = this.dragStart.panX + dx;
        this.panY = this.dragStart.panY + dy;
        return;
      }

      if (this.drawingMeasure && this.tool === 'measure') {
        const nat = this.stageToNatural(p);
        if (nat) this.activeMeasureEndNat = nat;
        this.hover.visible = false;
        return;
      }

      if (!this.imgMeta.renderW || !this.imgMeta.renderH || !this.imgMeta.naturalW || !this.labelIdArray) {
        this.hover.visible = false;
        return;
      }

      if (this.tool === 'eraser' && this.isErasing) {
        const now = Date.now();
        if (now - (this._lastEraseAt || 0) < 80) return;
        this._lastEraseAt = now;
        this.eraseAtEvent(evt);
        return;
      }

      if (this.tool === 'supplement') {
        this.hover.visible = false;
        const nat = this.stageToNatural(p);
        this.supplementCursorAllowed = this.isSupplementAllowedAtNatural(nat);
        return;
      }

      if (this.tool !== 'arrow' || this.viewMode !== 'filled') {
        this.hover.visible = false;
        return;
      }

      const nat = this.stageToNatural(p);
      if (!nat) {
        this.hover.visible = false;
        return;
      }

      const id = this.decodeIdAtNat(nat.x, nat.y);
      const m = id ? this.maskMap[id] : null;
      if (!m || !this.isMaskInCurrentRange(m)) {
        this.hover.visible = false;
        return;
      }

      const layer = this.$refs.hoverLayer;
      const w = layer?.clientWidth || this.imgMeta.renderW;
      const h = layer?.clientHeight || this.imgMeta.renderH;

      this.hover.visible = true;
      this.hover.areaPx = m.areaPx;
      this.hover.areaUnit = m.areaUnit;
      this.hover.areaUnitUnit = m.areaUnitUnit || null;
      this.hover.equivDiameter = m.equivDiameter;
      this.hover.x = Math.max(0, Math.min(p.x + 12, w - 160));
      this.hover.y = Math.max(0, Math.min(p.y + 12, h - 60));
    },
  },
};
</script>

<style scoped>
/* 与 step-1 保持一致的整体布局 */
.page {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
}

.content {
  display: grid;
  grid-template-columns: 180px 1fr 440px;
  grid-template-rows: 1fr;
  gap: 10px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.left {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 10px;
  overflow: auto;
}

.left-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.left-title {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
}

.ghost {
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 11px;
  cursor: pointer;
}

.history-block {
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.9);
  padding: 8px;
}

.history-title {
  font-size: 11px;
  color: #64748b;
  margin-bottom: 6px;
}

.history-empty {
  font-size: 11px;
  color: #94a3b8;
  padding: 6px 2px;
}

.history-item {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.95);
  padding: 0;
  margin-bottom: 8px;
  display: block;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
}

.history-item--preview-card {
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.history-item-card {
  padding: 8px;
}

.history-item-card-header {
  margin-bottom: 6px;
  min-width: 0;
}

.history-item-card-id {
  display: block;
  font-size: 11px;
  color: #334155;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-item-card-view {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #0b1220;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-item-card-view--empty {
  min-height: 100px;
}

.history-item-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.history-item-card-ph {
  font-size: 11px;
  color: #94a3b8;
  padding: 12px;
  text-align: center;
}

.history-item.active {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.meta-block {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.22);
  font-size: 11px;
  line-height: 1.45;
}

.meta-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 6px;
  padding: 3px 0;
}

.meta-row + .meta-row {
  border-top: 1px dashed rgba(148, 163, 184, 0.28);
}

.mk {
  color: #64748b;
}

.mv {
  color: #0f172a;
  word-break: break-all;
}

.center {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  align-items: stretch;
  height: 100%;
}

.topbar {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 10px;
}

.topbar-right {
  display: grid;
  grid-template-columns: minmax(120px, 0.75fr) minmax(120px, 0.75fr) minmax(280px, 1.6fr) minmax(320px, 1.9fr);
  align-items: stretch;
  gap: 10px 12px;
  width: 100%;
}

.canvas {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  overflow: hidden;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
}

.img-stage {
  position: relative;
  width: 100%;
  height: 100%;
  background: #0b1220;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* 与标尺识别页一致：contain 留白时背景渐变 */
.img-stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(900px 520px at 50% 40%, rgba(148, 163, 184, 0.22), rgba(11, 18, 32, 0) 60%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.85), rgba(11, 18, 32, 0.92));
  opacity: 1;
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #334155;
  white-space: normal;
  min-height: 38px;
  padding: 2px 0;
  justify-content: flex-start;
}

.size-filter {
  gap: 4px;
}

.range-input {
  width: 72px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  border-radius: 8px;
  padding: 5px 6px;
  font-size: 12px;
  outline: none;
}

.range-sep {
  color: #64748b;
}

.range-unit {
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

.range-current {
  color: #334155;
  font-size: 11px;
  white-space: nowrap;
}

.tool-label {
  font-weight: 800;
}

.tool-select {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  outline: none;
}

.tool-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 38px;
  padding: 2px 0;
  justify-content: flex-start;
}

.tool-btn {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
}

.tool-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.tool-btn-download {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.35);
  color: #3b82f6;
  font-weight: 800;
}

.mini-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(59, 130, 246, 0.22);
  border-top-color: #3b82f6;
  display: inline-block;
  animation: spin 0.8s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}

.img-transform {
  position: absolute;
  transform-origin: 0 0;
  pointer-events: none; /* 让事件交给 hover-layer */
  z-index: 1;
  transition: opacity 220ms ease, filter 220ms ease;
}

.img-transform.range-transition {
  opacity: 0.45;
  filter: saturate(0.85);
}

.canvas-img {
  display: block;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #0b1220;
}

.canvas-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.canvas-ph {
  color: #64748b;
  font-size: 12px;
  padding: 16px;
}

.global-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.global-loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #e5e7eb;
  font-size: 12px;
}

.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(226, 232, 240, 0.5);
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

.loading-text {
  letter-spacing: 0.5px;
}

.hover-layer {
  position: absolute;
  inset: 0;
  touch-action: none;
  z-index: 3;
}

.measure-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.measure-line {
  stroke: #3b82f6;
  stroke-width: 2;
}

.measure-line-active {
  stroke-dasharray: 6 4;
}

.measure-text {
  font-size: 12px;
  fill: #ecfeff;
  paint-order: stroke;
  stroke: rgba(15, 23, 42, 0.8);
  stroke-width: 3px;
  stroke-linejoin: round;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.tooltip {
  position: absolute;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12px;
  color: #0f172a;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
}

.tt-line {
  line-height: 1.5;
}

.footer {
  display: flex;
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 4px;
  gap: 10px;
}

.secondary {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
}

.right .panel {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 12px;
  height: 100%;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 13px;
  font-weight: 900;
  margin-bottom: 10px;
}

.notice {
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.guide-table {
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 12px;
  overflow: hidden;
}

.guide-table .row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  padding: 9px 10px;
  font-size: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.guide-table .row.head {
  background: rgba(241, 245, 249, 0.8);
  font-weight: 700;
  border-top: none;
}

@media (max-width: 1100px) {
  .content {
    grid-template-columns: 1fr;
  }

  .topbar {
    flex-wrap: wrap;
  }

  .topbar-right {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .right .panel {
    min-height: 0;
  }

  .canvas {
    min-height: 420px;
  }
}

@media (max-width: 760px) {
  .topbar-right {
    grid-template-columns: 1fr;
  }
}
</style>

